import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { FOOTER_NAV, isNavItemActive, NAV_SECTIONS, type NavSection } from './navItems';
import { SidebarItem } from './SidebarItem';
import styles from './Sidebar.module.css';

const COLLAPSED_KEY = 'focusmode.sidebar.collapsed';

function readCollapsed(): string[] {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    // First visit opens everything. This line used to collapse every
    // collapsible section by default, which put ממשל, Skills, אקדמיה and
    // פרומפטים behind a closed heading on any fresh browser or profile — and
    // it was reported as "the academy was deleted". Collapsing is a choice the
    // user makes and we remember, never one we make for them.
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeCollapsed(ids: string[]) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(ids));
  } catch {
    // Private mode or blocked storage: the sidebar still works, it just forgets.
  }
}

function BrandLogo() {
  return (
    <svg viewBox="0 0 100 100" width="32" height="32" aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="#FFCD2E" stroke="#0E0E10" strokeWidth="3.5" />
      <path
        d="M30 42c2-2 6-2 8 0M62 42c2-2 6-2 8 0"
        stroke="#0E0E10"
        strokeWidth="4.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M36 60c4 5 10 7 14 7s10-2 14-7"
        stroke="#0E0E10"
        strokeWidth="4.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface Props {
  orgName: string | null;
  open: boolean;
  onNavigate: () => void;
}

export function Sidebar({ orgName, open, onNavigate }: Props) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);

  function toggle(section: NavSection) {
    const next = collapsed.includes(section.id)
      ? collapsed.filter((id) => id !== section.id)
      : [...collapsed, section.id];
    setCollapsed(next);
    writeCollapsed(next);
  }

  return (
    <aside className={cn(styles.sidebar, open && styles.open)} aria-label="ניווט ראשי">
      <div className={styles.brand}>
        <BrandLogo />
        <div className={styles.brandText}>
          <span className={styles.brandTitle}>FocusMode</span>
          {orgName ? <span className={styles.brandOrg}>{orgName}</span> : null}
        </div>
      </div>

      <nav className={styles.scroll}>
        {NAV_SECTIONS.map((section) => {
          // A collapsed section still opens when you are on one of its pages,
          // otherwise the active item would be invisible.
          const holdsActive = section.items.some((item) => isNavItemActive(item, pathname));
          const isOpen = !section.collapsible || !collapsed.includes(section.id) || holdsActive;
          const listId = `sidebar-section-${section.id}`;

          return (
            <div key={section.id} className={styles.section}>
              {section.label ? (
                section.collapsible ? (
                  <button
                    type="button"
                    className={cn(styles.sectionLabel, styles.sectionToggle)}
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={() => {
                      toggle(section);
                    }}
                  >
                    <span>{section.label}</span>
                    <ChevronDown
                      size={14}
                      aria-hidden="true"
                      className={cn(styles.sectionChevron, !isOpen && styles.sectionChevronClosed)}
                    />
                  </button>
                ) : (
                  <span className={styles.sectionLabel}>{section.label}</span>
                )
              ) : null}
              {isOpen ? (
                <div id={listId} className={styles.sectionItems}>
                  {section.items.map((item) => (
                    <SidebarItem key={item.id} item={item} onNavigate={onNavigate} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        <hr className={styles.divider} />

        <div className={styles.section}>
          {FOOTER_NAV.map((item) => (
            <SidebarItem key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </nav>

      <div className={styles.foot}>v0.1 · Phase 0</div>
    </aside>
  );
}
